/**
 * MindUP K-2 Curriculum Data
 * 
 * Structured curriculum data for Grades K-2 lessons
 * Based on The Goldie Hawn Foundation MindUP program
 */

export interface MindUPLesson {
  id: string;
  unit: number;
  unitTitle: string;
  lessonNumber: number;
  gradeLevel?: string;
  title: string;
  purpose: string;
  concepts: string[];
  objectives: string[];
  socialEmotionalLearning: string[];
  materials: string[];
  estimatedTime: string;
  content: {
    brainLink?: string;
    gettingStarted?: string[];
    activities?: Array<{
      step: number;
      title: string;
      instructions: string[];
    }>;
    makingConnections?: string[];
    followingUp?: {
      mindupForHomeAndLife?: string[];
      mindfulMePortfolio?: string;
    };
    simplificationIdeas?: string[];
    challengerIdeas?: string[];
    literatureList?: string[];
  };
}

export const K2_CURRICULUM: MindUPLesson[] = [
  {
    id: "k2-u1-l1",
    unit: 1,
    unitTitle: "My Mindful Brain",
    lessonNumber: 1,
    title: "Building the MindUP Learning Community",
    purpose: "A caring environment helps children feel a sense of belonging and connection to their peers and educators. The purpose of Lesson 1 is to foster a safe, kind, and focused learning environment and build student-to-student and student-to-teacher connections.",
    concepts: [
      "I can contribute to our learning community by being safe, kind, and focused."
    ],
    objectives: [
      "Establish a common understanding of what it means to feel safe, kind, and focused in the classroom.",
      "Collaborate and record strategies to contribute to their MindUP learning community.",
      "Set a foundation that will help students reflect on future MindUP lessons and add to the MindUP learning community strategies."
    ],
    socialEmotionalLearning: [
      "Self-awareness",
      "Relationship skills",
      "Self-management",
      "Responsible decision-making",
      "Social awareness"
    ],
    materials: [
      "Chart paper and markers",
      "Mindful Me Portfolio (basic lined notebook or a folder for portfolio entries of differing media; 1 per student)"
    ],
    estimatedTime: "20–30 minutes",
    content: {
      brainLink: "Relationships are essential, not just for our happiness, but for our ability to learn and grow in positive ways. For children and youth, feeling a sense of belonging and connectedness with peers and adults predicts learning outcomes, well-being, and life satisfaction.",
      gettingStarted: [
        "Review of MindUP Learning Community Agreement",
        "Activity: Class Discussion to Access Prior Knowledge"
      ],
      activities: [
        {
          step: 1,
          title: "Brainstorm Activity",
          instructions: [
            "Invite students to think, pair, and share the type of learning environment that they need in order to feel safe, kind, and focused.",
            "Encourage positive framing, asking what is it we DO want to see in our community instead of what we DON'T want.",
            "Repeat the procedure for 'kind' and 'focused'."
          ]
        },
        {
          step: 2,
          title: "MindUP Learning Community Agreement",
          instructions: [
            "Create a MindUP learning community agreement poster on chart paper.",
            "Record five or six ideas generated by the students about what their safe, kind, and focused learning community looks, feels, and sounds like."
          ]
        }
      ],
      makingConnections: [
        "Summarize Key Points",
        "Application of New Knowledge to MindUP Learning Community Agreement"
      ],
      followingUp: {
        mindupForHomeAndLife: [
          "Invite students to share a story or a drawing of what it means to be safe, kind, and focused with their families or caregivers."
        ],
        mindfulMePortfolio: "Introduce the Mindful Me Portfolio, and allow students some time to customize the cover and/or create their first entry reflecting on their learning community."
      },
      simplificationIdeas: [
        "Invite students to brainstorm things they are good at.",
        "Have students draw or write things they are good at, and give them opportunities to share.",
        "Encourage students to brainstorm with people in their school community."
      ],
      challengerIdeas: [
        "Have students think about ways they can contribute to their school community to help it be more safe, kind, and focused.",
        "Invite community members to come and present to the class.",
        "Explore what the community members contribute to the community.",
        "Encourage students to consider goals they can work toward and share these with others."
      ],
      literatureList: [
        "All Are Welcome – Alexandra Penfold",
        "Love the World – Todd Parr",
        "You Lift Me Up – Monique Gray Smith",
        "Brave – Stacy McAnulty",
        "All Kinds of Families! – Mary Ann Hoberman",
        "Be Kind – Pat Zietlow Miller & Jen Hill",
        "It's Okay to Be Different – Todd Parr",
        "Thunder Boy Jr. – Sherman Alexie",
        "Give Thank You a Try – James Patterson"
      ]
    }
  },
  {
    id: "k2-u1-l2",
    unit: 1,
    unitTitle: "My Mindful Brain",
    lessonNumber: 2,
    title: "My Amazing Brain",
    purpose: "Students are introduced to the core theme of the MindUP program: the brain and how they can use it to focus their attention and calm down. They are introduced to three key brain structures that help them do that: the amygdala, hippocampus, and prefrontal cortex (PFC).",
    concepts: [
      "I can use my brain to focus my attention and calm down.",
      "Three parts of my brain help me stay safe, calm down, and focus and learn: the amygdala, hippocampus, and PFC."
    ],
    objectives: [
      "Identify the amygdala, hippocampus, and PFC on a diagram of the brain.",
      "Define the basic functions of the amygdala, hippocampus, and PFC.",
      "Consider how their new knowledge about the brain may inform their MindUP learning community agreement and daily activities."
    ],
    socialEmotionalLearning: [
      "Self-awareness",
      "Self-management",
      "Social awareness",
      "Responsible decision-making"
    ],
    materials: [
      "MindUP Brain poster (downloadable from the course module along with this lesson)",
      "My Mindful Brain handout (located at the end of this lesson)",
      "Mindful Me Portfolio"
    ],
    estimatedTime: "40–45 minutes",
    content: {
      brainLink: "Our brains change throughout our lives in response to our environment and experience. This is called neuroplasticity. It means we can play a role in how our brain changes and develops based on what we choose to do.",
      gettingStarted: [
        "Review of MindUP Learning Community Agreement",
        "Activity: Class Discussion to Access Prior Knowledge"
      ],
      activities: [
        {
          step: 1,
          title: "Name and Locate the Parts of the Brain",
          instructions: [
            "Point to each part of the brain on the MindUP Brain poster as you name it.",
            "Have students touch the center of their foreheads, and explain that the PFC is just behind.",
            "Have students touch their ears, and explain that the hippocampus is just behind.",
            "Point to the amygdala on the brain poster, and explain that it is located deep in the middle of the brain."
          ]
        },
        {
          step: 2,
          title: "Functions of the Parts of the Brain",
          instructions: [
            "Explain the function of each brain part. Have students touch where the brain part is located as you explain.",
            "PFC: This part of your brain helps you make good choices, pay attention, and learn. The PFC is like your Wise Owl.",
            "Hippocampus: The hippocampus helps us remember things. Its job is to decide which memories to store and to find them when we need them. Your hippocampus is like your Elephant who has a great memory!",
            "Amygdala: This part of the brain doesn't think! It just reacts. Your amygdala is like your Guard Dog. It helps keep you safe!"
          ]
        },
        {
          step: 3,
          title: "My Mindful Brain Worksheet",
          instructions: [
            "Hand out the My Mindful Brain worksheet, and ask students to label the PFC, hippocampus, and amygdala.",
            "When they are done labeling, invite students to draw a picture of themselves and their mindful brain."
          ]
        }
      ],
      makingConnections: [
        "Summarize the Key Points",
        "Review MindUP Learning Community Agreement"
      ],
      followingUp: {
        mindupForHomeAndLife: [
          "As a class, review what students have learned about the three parts of the brain.",
          "Ask students to notice their thoughts throughout their day and think about which parts of the brain they are using in different scenarios."
        ],
        mindfulMePortfolio: "Invite students to write or draw about how their brain reacts in different scenarios in their lives."
      },
      simplificationIdeas: [
        "Introduce the brain by exploring where it is in the body.",
        "Introduce only one part of the brain at a time (one per lesson).",
        "Teach a song or poem about the brain.",
        "Read a simple science book about the brain."
      ],
      challengerIdeas: [
        "Make large laminated images of each of the three parts of the brain and label them. Have students throw a beanbag at each part of the brain, identify it, and describe its function.",
        "Use salt dough to create small models of the brain."
      ],
      literatureList: [
        "A Walk in the Rain with a Brain – Edward M. Hallowell, MD",
        "The Brain Is Kind of a Big Deal – Nick Seluk",
        "Brain Puzzles – Sarah Khan",
        "See Inside Your Head – Alex Frith & Colin King",
        "Understanding Your Brain – Rebecca Treays",
        "What Goes On in My Head? – Robert Winston",
        "Your Fantastic Elastic Brain – JoAnn Deak, PhD",
        "Brain and Senses – Jen Green"
      ]
    }
  },
  {
    id: "k2-u1-l3",
    unit: 1,
    unitTitle: "My Mindful Brain",
    lessonNumber: 3,
    title: "Learning to Be Mindful",
    purpose: "Students are introduced to the concepts of being mindful and unmindful and relate them to their own experience.",
    concepts: [
      "Being mindful means paying attention to what is happening now with curiosity.",
      "Being unmindful means not paying attention to what is happening now."
    ],
    objectives: [
      "Describe the difference between being mindful and being unmindful.",
      "Articulate how experiences in their own lives relate to the concepts of being mindful and unmindful."
    ],
    socialEmotionalLearning: [
      "Self-awareness",
      "Self-management",
      "Social awareness",
      "Relationship skills",
      "Responsible decision-making"
    ],
    materials: [
      "MindUP Brain poster",
      "Chart paper or whiteboard",
      "Mindful Me Portfolio"
    ],
    estimatedTime: "25–30 minutes",
    content: {
      brainLink: "The prefrontal cortex (PFC), through its connections to virtually all other brain regions, serves to guide, direct, integrate, and monitor goal-directed behavior. Taking time to process new input from the senses (listening, savoring, describing) helps the brain experience a time interval between immediate reaction and conscious thinking. This delay increases children's ability to react to new sensory input more reflectively.",
      gettingStarted: [
        "Spend a few minutes reviewing what students learned about the brain last time, and briefly remind the class about their co-constructed MindUP learning community agreement.",
        "Give an overview of the program to your class: In the MindUP Program, we will learn about our brains and mindful awareness, and we'll try activities to help us focus our attention. We will also learn about our feelings and ways to live a happier life."
      ],
      activities: [
        {
          step: 1,
          title: "Experiential Activity",
          instructions: [
            "Tell the students that they are going to practice focusing on one sense—listening—by closing their eyes and noticing all the sounds they can hear.",
            "Ask students to sit comfortably, close their eyes or look down at their hands, and listen to the sounds around them in the classroom, the hallway, outside, and in the school.",
            "After 30 seconds, ask students to look up and share the sounds they heard. Record students' responses on the board or chart paper.",
            "Repeat the listening activity two more times. Each time, have students try to notice sounds they might normally miss."
          ]
        },
        {
          step: 2,
          title: "Reflecting on the Experience",
          instructions: [
            "Discuss with students how many sounds they heard when they really focused their attention.",
            "When we focus our attention, we are being mindful.",
            "The opposite is when we aren't paying attention or are just not aware of what we are doing or what is going on around us—this is being unmindful."
          ]
        },
        {
          step: 3,
          title: "Define Mindful Awareness for the Class",
          instructions: [
            "Engage students in a discussion about mindful awareness.",
            "Mindful awareness is when we notice what is happening each moment—what we notice around us, and what we think and feel.",
            "It also means we pay attention to each moment with curiosity, without judgment.",
            "Being mindful means to pay attention on purpose. Being mindful means being curious about what's around you. Being mindful is a little bit like being a scientist."
          ]
        },
        {
          step: 4,
          title: "Initiate Class Discussion",
          instructions: [
            "Have students share personal examples of being unmindful.",
            "Our Wise Owl, the PFC, can only focus on one thing at a time. By being mindful, we are practicing how to help our Wise Owl pay attention to what we choose, like learning or listening to a friend.",
            "Remember that learning to be mindful takes practice, just like learning to play a new instrument or a sport."
          ]
        }
      ],
      makingConnections: [
        "The definition of mindful awareness is 'paying attention the best way we can.'",
        "When we are not paying attention, this is called 'being unmindful.'",
        "When we're mindful, we use our PFC. This helps us make better choices.",
        "Invite students to apply their new knowledge about mindful awareness to their shared learning community agreement."
      ],
      followingUp: {
        mindupForHomeAndLife: [
          "As a class, brainstorm scenarios in which students can practice being mindful in their daily lives.",
          "Possible scenarios: Eating a meal or a snack, Walking to school, Riding your bike, Playing in the park, Playing with a friend, Doing chores, Brushing your teeth",
          "Students will have the opportunity to reflect on their experiences in the days that follow in an entry for their Mindful Me Portfolio."
        ],
        mindfulMePortfolio: "Ask students to create an entry for their Mindful Me Portfolio that captures them in a mindful moment. This could take the form of a picture, poem, song, paragraph, photograph, etc.—the sky's the limit!"
      },
      simplificationIdeas: [
        "Implement the main listening activity using music or simple instruments.",
        "Model mindful awareness by sharing what you can see, hear, and notice in a given moment.",
        "Share ideas about what daily classroom routines can be done with mindful awareness."
      ],
      challengerIdeas: [
        "Invite students to look at picture books 'like a scientist' to notice small details they might usually miss.",
        "Following the Mindful Living activity, invite students to write a Mindful Me Portfolio entry about one thing they tried to do with mindful awareness."
      ],
      literatureList: [
        "A Bear and His Boy – Sean Bryan",
        "A Quiet Place – Douglas Wood",
        "What Is a Thought? – Jack Pransky & Amy Kahofer",
        "The Three Questions – Jon J Muth",
        "Zen Shorts – Jon J Muth",
        "What Does It Mean to Be Present? – Rana DiOrio & Eliza Wheeler",
        "The Girl and the Bicycle – Mark Pett",
        "The Mindfulness Room - Amanda Loraine Lynch",
        "Shi-shi-etko - Nicola I. Campbell"
      ]
    }
  },
  {
    id: "k2-u1-l4",
    unit: 1,
    unitTitle: "My Mindful Brain",
    lessonNumber: 4,
    title: "The Brain Break",
    purpose: "Students are introduced to the core mindful awareness practices of the MindUP program Brain Break: mindful listening and mindful breathing. Students will continue to do these practices throughout the program.",
    concepts: [
      "I can practice being mindful with mindful listening and breathing.",
      "When I focus my attention on the present moment, I'm using my prefrontal cortex (PFC)."
    ],
    objectives: [
      "Practice mindful listening and breathing."
    ],
    socialEmotionalLearning: [
      "Self-awareness",
      "Self-management",
      "Responsible decision-making"
    ],
    materials: [
      "Chime",
      "Mindful Me Portfolio"
    ],
    estimatedTime: "25–30 minutes",
    content: {
      brainLink: "In this lesson, students learn how to take a Brain Break. Specifically, in the MindUP Brain Break, students will attend to an external stimulus (sound of instrument) and an internal stimulus (breath). This helps train attention and build mindful awareness, which facilitates emotional balance and attention.",
      gettingStarted: [
        "Ask students to define being mindful and unmindful",
        "Set norms for mindful practices",
        "Ask if anyone would like to share their experiences with the MindUP for Home and Life activity from the last lesson"
      ],
      activities: [
        {
          step: 1,
          title: "Mindful Listening Activity",
          instructions: [
            "Ask students to sit comfortably in a circle.",
            "Ask students to close their eyes if they feel comfortable doing so.",
            "Ask students to place their hands gently in their laps, and instruct them to focus the best way they can and listen to the sound of the instrument for as long as they can.",
            "When you can no longer hear the sound, you can quietly raise your hand.",
            "Repeat one or two times."
          ]
        },
        {
          step: 2,
          title: "Brain Break—Mindful Breathing Activity",
          instructions: [
            "Ask students to remain seated. If students feel comfortable, they may close their eyes, or they may look down at their hands.",
            "Begin and end the Brain Break with one ring of the chime.",
            "Students should learn to breathe in through their nose and then slowly exhale.",
            "Guide students through the Brain Break script for 30 seconds to 1 minute."
          ]
        }
      ],
      makingConnections: [
        "Sharing Experiences",
        "Summarize the Key Points",
        "Application of New Knowledge to MindUP Learning Community Agreement"
      ],
      followingUp: {
        mindupForHomeAndLife: [
          "As a class, brainstorm ways in which mindful awareness can be applied to students' daily lives.",
          "Remind students: Your breath is always with you, so you can take a brain break whenever you need!"
        ],
        mindfulMePortfolio: "Invite students to create a Mindful Me Portfolio entry about their experience with mindful breathing in their daily lives."
      },
      simplificationIdeas: [
        "Listen to the chime for only a few seconds.",
        "Use the Hoberman sphere or your hands to demonstrate breath going in and out.",
        "Focus on one object for only a few seconds.",
        "Introduce the 'Mind Jar.'"
      ],
      challengerIdeas: [
        "Challenge the students to lengthen the breathing (Brain Break) time.",
        "Invite students to try to lengthen their exhale during the Brain Break.",
        "Get deeper into how the neurons work during the Brain Break practice."
      ],
      literatureList: [
        "Anh's Anger – Gail Silver",
        "Jabari Jumps – Gaia Cornwall",
        "Frog's Breathtaking Speech: How Children (and Frogs) Can Use the Breath to Deal with Anxiety, Anger, and Tension – Michael Chissick & Sarah Peacock",
        "Breathe – Scott Magoon",
        "Visiting Feelings – Laura Rubenstein",
        "Each Breath a Smile – Sister Susan (based on teachings by Thich Nhat Hanh)",
        "Alphabreaths – Christopher Willard & Daniel Rechetschatffen"
      ]
    }
  },
  // Unit 2: Mindful Senses
  {
    id: "k2-u2-l5",
    unit: 2,
    unitTitle: "Mindful Senses",
    lessonNumber: 5,
    title: "Mindful Listening",
    purpose: "Students practice being mindful by using the sense of hearing to focus their attention on external sounds.",
    concepts: [
      "Mindful listening helps me practice focusing my attention.",
      "I can use mindful listening as a Brain Break."
    ],
    objectives: [
      "Practice focusing their attention with the sense of hearing.",
      "Identify sounds without judgment."
    ],
    socialEmotionalLearning: [
      "Self-awareness",
      "Social awareness",
      "Self-management"
    ],
    materials: [
      "Variety of sounds (crumpling paper, tapping pencil, shaking coins, shuffling cards, etc.) OR nature sounds from soundjay.com",
      "Chart paper",
      "Mindful Me Portfolio"
    ],
    estimatedTime: "20–25 minutes",
    content: {
      brainLink: "Most types of reward (e.g., food, pleasurable music) have been shown to increase the level of dopamine in the brain. When dopamine levels increase, pleasure, motivation, alert awareness, perseverance, memory, and resilience become more prevalent. Regular practice of mindful awareness activities activates the prefrontal cortex and helps strengthen neural connections that relate to attention.",
      gettingStarted: [
        "Remind the class about your agreed-upon ways of being together to create a safe, kind, and focused learning community.",
        "Ask the class to recall the previous lessons and define being mindful and unmindful.",
        "Lead a class discussion by asking: Why is it important to listen? How is listening important for schoolwork? How can listening make us happy? Does listening help with friendships? Why do you think it is important to practice listening?"
      ],
      activities: [
        {
          step: 1,
          title: "Mindful Listening",
          instructions: [
            "Ask students to sit comfortably in a circle. Invite students to close their eyes if they feel comfortable.",
            "Choose a sound from your prepared list (crumpling paper, tapping pencil, shaking coins, shuffling cards, etc.).",
            "Ask students to rest their hands gently on their desk or on their lap.",
            "Read the script: Listen to the sound I make until you can no longer hear it.",
            "Make the sound. Then read: Now, in your mind, try to guess what the sound is. You can keep your ideas in your head and share them later.",
            "Make the sound again.",
            "Read: You may open your eyes and write down or draw what you think the sound was.",
            "Repeat the above exercise with each of the sounds you selected."
          ]
        },
        {
          step: 2,
          title: "Class Discussion",
          instructions: [
            "Lead a class discussion by asking: Is this how we usually listen to sounds? How is it different? Were you able to maintain your focus on the sound? Did anything get in the way? Do you think it might be helpful to listen this way more often? Would practice be useful? How do you think this type of listening affects your brain? (Hint: You use your PFC!)"
          ]
        },
        {
          step: 3,
          title: "MindUP Learning Community Agreement",
          instructions: [
            "Lead a class discussion by asking: How can mindful listening help our learning community be safe? How can mindful listening help our learning community be kind? How can mindful listening help our learning community be focused?",
            "Add one or two ideas to your MindUP Learning Community Agreement poster.",
            "After children have noted their ideas, have a short sharing discussion, and then reveal the source of the sound."
          ]
        }
      ],
      makingConnections: [
        "Mindful listening is another mindful practice that helps us focus our attention.",
        "We often don't take time to listen mindfully. Listening is an important sense that helps us be present in our daily experiences.",
        "By taking the time to listen mindfully, we can train our attention and be more fully present in our experiences."
      ],
      followingUp: {
        mindupForHomeAndLife: [
          "As a class, brainstorm ways in which mindful listening applies to students' daily living.",
          "Bring your attention to the sounds around you at different times during your day: What do you hear when you first wake up? At the breakfast table? On your way to school? Over the lunch break? At your after-school activity?",
          "Listen carefully to songs/music playing around you—what parts do you love the most, and why?",
          "Pay attention to the sounds you hear when you are in these settings. If you are ready to reflect on what you heard, record your discoveries in your Mindful Me Portfolio."
        ],
        mindfulMePortfolio: "Invite students to create a portfolio entry about their experience with the MindUP for Home and Life discussion topic: mindful listening in their daily lives. This entry could take the form of a picture, poem, song, paragraph, photograph, etc.—the sky's the limit!"
      },
      simplificationIdeas: [
        "Play the telephone game. Students sit in a circle. The first student whispers a short, simple phrase into the ear of the student sitting next to them. Then that student whispers what they hear to the next student, and so on.",
        "Invite students to listen to a variety of simple clapping patterns and then repeat them back to you."
      ],
      challengerIdeas: [
        "Go on a nature walk, and listen mindfully to the sounds outside. Record what you hear. Draw what you hear.",
        "Play the mystery sounds game in class, progressing from simple, easily identifiable sounds to more challenging ones."
      ],
      literatureList: [
        "Listen! Listen! – Ann Rand & Paul Rand",
        "Look, Listen and Learn – Susan Canizares & Pamela Chanko",
        "Silence – Lemniscates",
        "The Listening Walk – Paul Showers",
        "The Quiet Book – Deborah Underwood",
        "The Loud Book – Deborah Underwood"
      ]
    }
  },
  // Lesson 6: Mindful Seeing
  {
    id: "k2-u2-l6",
    unit: 2,
    unitTitle: "Mindful Senses",
    lessonNumber: 6,
    title: "Mindful Seeing",
    purpose: "Students practice being mindful by using the sense of sight to focus their attention on an external object.",
    concepts: [
      "Mindful seeing helps me practice focusing my attention.",
      "It's okay to have trouble holding my attention on something."
    ],
    objectives: [
      "Practice focusing their attention with the sense of sight.",
      "Recall visual details about an object using nonjudgmental language."
    ],
    socialEmotionalLearning: [
      "Self-awareness",
      "Social awareness",
      "Self-management"
    ],
    materials: [
      "Limes, oranges, marbles, or pebbles (one per student; all students should have the same type of object)",
      "Mindful Me Portfolios"
    ],
    estimatedTime: "20–25 minutes",
    content: {
      brainLink: "The RAS is the lower part of the posterior brain that filters all sensory inputs and 'decides' what to pay attention to—and what to ignore. When children practice concentrating on details for extended periods of time, they develop more conscious control of what input they focus on.",
      gettingStarted: [
        "Remind the class about your agreed-upon ways of being together to create a safe, kind, and focused learning community.",
        "Invite a brief class discussion about the previous lesson, Mindful Listening.",
        "Ask if anyone would like to share their experiences with the listening lesson or the MindUP for Home and Life activity."
      ],
      activities: [
        {
          step: 1,
          title: "Mindful Seeing",
          instructions: [
            "Ask students to sit comfortably in a circle.",
            "Give each student an object (lime, marble, etc.).",
            "Guide students through the activity: Take your object and hold it in your palm. Get to know your object the best you can. What does it look like? What color(s) do you see? What do you notice about the shape? What does the texture look like?",
            "Take your time, and focus the best you can on your object. If your mind wanders away from your object, that's okay! Just bring your attention back to your object.",
            "Ring the chime. Please listen as long as you can, and then bring your attention back to the room."
          ]
        },
        {
          step: 2,
          title: "Applying Focused Attention",
          instructions: [
            "Collect all of the objects from each student into a basket or box, and mix them up.",
            "Spread the objects out, and ask students (one at a time) to come up and find their object!",
            "Note: Ensure that children understand that they are not being assessed for finding the correct object."
          ]
        }
      ],
      makingConnections: [
        "Mindful seeing helps us practice focusing our attention.",
        "Remember, having trouble holding our attention on something is a normal and expected step when we practice being mindful.",
        "By paying attention the best way we can to our present experience, we can understand things more fully.",
        "Invite students to consider how mindful seeing can contribute to a safe, kind, and focused learning community."
      ],
      followingUp: {
        mindupForHomeAndLife: [
          "As a class, brainstorm ways in which mindful seeing applies to students' daily living.",
          "Bring your attention to the sights you see around you at different times during your day: What do you see when you first wake up? On your way to school? Up in the sky? On a nature walk? In the expression on your friend's face? In the pages of a book?",
          "Pay attention to these sights when you are in these settings. If you are ready to reflect on what you saw, record your discoveries in your Mindful Me Portfolio."
        ],
        mindfulMePortfolio: "Invite students to create a portfolio entry about their experience with the MindUP for Home and Life topic, mindful seeing. This entry could take the form of a picture, poem, song, paragraph, photograph, etc.—the sky's the limit!"
      },
      simplificationIdeas: [
        "Play 'I Spy' with the class.",
        "Explore objects around the room with magnifying glasses.",
        "Identify simple shapes/colors around the room.",
        "Have each student look in a mirror and mindfully look at themselves—their eye color, hair style and color, clothing, etc.—and then draw themselves."
      ],
      challengerIdeas: [
        "Introduce 'Where's Waldo?' books and associated card games.",
        "Create 'mindful drawing' books where the students can draw objects with close attention to the details.",
        "Create eyeglasses out of construction paper. The students can put them on to notice details about things around them."
      ],
      literatureList: [
        "Look, Listen and Learn – Susan Canizares & Pamela Chanko",
        "The Blind Men and the Elephant – Karen Backstein",
        "What the Sun Sees, What the Moon Sees – Nancy Tafuri",
        "The Snowy Day – Ezra Jack Keats",
        "I Spy (series) – Jean Marzollo"
      ]
    }
  },
  // Lesson 7: Mindful Smelling
  {
    id: "k2-u2-l7",
    unit: 2,
    unitTitle: "Mindful Senses",
    lessonNumber: 7,
    title: "Mindful Smelling",
    purpose: "Students practice being mindful by using the sense of smell to focus their attention on external items.",
    concepts: [
      "Mindful smelling helps me practice focusing my attention.",
      "I can describe smells without judging if I like them or not.",
      "Smells can remind me of different things I've experienced in the past."
    ],
    objectives: [
      "Practice focusing their attention with the sense of smell.",
      "Identify scents and any thoughts or feelings that arise without judgment."
    ],
    socialEmotionalLearning: [
      "Self-awareness",
      "Social awareness",
      "Self-management"
    ],
    materials: [
      "Small, opaque containers (e.g., empty milk cups, plastic baggies, baby food jars) with perforated lids to hold scents, each container labeled with a letter; one for each scent sample",
      "4 scent samples, such as coffee beans, vinegar-soaked cotton balls, cinnamon bark, ginger root, lemon, dill, etc.",
      "Chart paper",
      "Mindful Me Portfolio"
    ],
    estimatedTime: "25–30 minutes",
    content: {
      brainLink: "In this lesson, students describe a mystery scent and notice if it reminds them of a memory or a feeling. A central aspect of mindfulness is learning the difference between noticing and judging. Mindful smelling can be a great tool to practice this distinction. If children do not feel safe, new sensory input may trigger the amygdala to initiate the survival mechanism of 'fight, flight, or freeze.'",
      gettingStarted: [
        "Remind the class about your agreed-upon ways of being together to create a safe, kind, and focused learning community.",
        "Invite a brief class discussion about the previous lesson, Mindful Seeing.",
        "Ask if anyone would like to share their experiences with the MindUP for Home and Life activity."
      ],
      activities: [
        {
          step: 1,
          title: "Mindful Smelling",
          instructions: [
            "Read the script: Today, we are going to do a science experiment to help us bring mindful awareness to what we smell. I have some containers with mystery scents for you to explore.",
            "Brainstorm 'smelling' vocabulary as a group.",
            "Read the script: Try to be curious—even with those smells that are not pleasant for you. Try to just smell them mindfully, as they are, and describe them. What thoughts, feelings, or memories does the smell bring up for you? What do you think the smell is?",
            "Have students sit in a circle, and instruct them to pass around the first mystery scent, giving each person a chance to smell it. Encourage them to keep their thoughts and feelings to themselves until everyone has had a chance to smell the mystery scent.",
            "When everyone has had a chance to smell the mystery scent, record their thoughts and feelings on the board or chart paper under the heading 'Mystery Scent #1.'",
            "Repeat this for the rest of the scents."
          ]
        },
        {
          step: 2,
          title: "Sharing Experiences",
          instructions: [
            "Initiate a brief class discussion based on the responses to each scent, and reveal the name of each scent."
          ]
        }
      ],
      makingConnections: [
        "Mindful smelling is another mindful practice that helps us focus our attention.",
        "We often don't take time to smell things mindfully. Smell is an important sense that helps us be present in our daily experiences.",
        "Smells can remind us of memories from long ago or everyday things.",
        "By taking the time to smell mindfully, we can train our attention and be more fully present in our experiences.",
        "Invite students to consider how mindful smelling can contribute to a safe, kind, and focused classroom."
      ],
      followingUp: {
        mindupForHomeAndLife: [
          "As a class, brainstorm ways in which mindful smelling might apply to students' daily living.",
          "Bring your attention to the smells around you at different times during your day: What do you smell first thing in the morning? On your way to and from school? During the lunch hour? At dinner time? On a nature walk or in the garden?",
          "Pay attention to these smells when you are in these settings. If you are ready to reflect on your experiences, record your discoveries in a journal, in your sketchbook, etc., and refer to them for the Mindful Me Portfolio entry."
        ],
        mindfulMePortfolio: "Invite students to create a portfolio entry about their experience with the MindUP for Home and Life topic, mindful smelling. This entry could take the form of a picture, poem, song, paragraph, photograph, etc.—the sky's the limit!"
      },
      simplificationIdeas: [
        "Begin this lesson using only one familiar smell. Have the students describe the smell as a group on a chart. Do one smell a day to build vocabulary."
      ],
      challengerIdeas: [
        "Bring in more challenging mystery smells for students to explore and identify.",
        "Discuss and chart the different smells they encounter in different locations, such as the lunchroom, library, gym, bathroom, outside, at home, etc.",
        "Have a group discussion about what different smells remind them of, and connect this to the hippocampus and the role it plays in the brain."
      ],
      literatureList: [
        "Thank You, Omu! – Oge Mora",
        "I Hear a Pickle – Rachel Isadora",
        "What Does It Mean to Be Present? – Rana DiOrio & Eliza Wheeler",
        "The Sense of Smell – Ellen Weis",
        "Mo Smells Red – Margaret Hyde",
        "Smell (Five Senses Series) – Maria Rius",
        "A Sweet Smell of Roses – Angela Johnson & Eric Velasquez",
        "Smelling (Five Senses Series) – Rebecca Rismon",
        "Skunkdog – Emily Jenkins"
      ]
    }
  },
  // Lesson 8: Mindful Tasting
  {
    id: "k2-u2-l8",
    unit: 2,
    unitTitle: "Mindful Senses",
    lessonNumber: 8,
    title: "Mindful Tasting",
    purpose: "Students practice being mindful by using the senses of sight, smell, and taste to focus their attention on external items.",
    concepts: [
      "Mindful tasting helps me practice focusing my attention.",
      "Eating mindfully helps me slow down and enjoy the experience more."
    ],
    objectives: [
      "Practice focusing their attention with the senses of sight, smell, and taste.",
      "Describe how mindful tasting differs from typical tasting experiences."
    ],
    socialEmotionalLearning: [
      "Self-awareness",
      "Social awareness",
      "Self-management"
    ],
    materials: [
      "Raisins or small pieces of fruit (one per student)",
      "Mindful Me Portfolio"
    ],
    estimatedTime: "20–25 minutes",
    content: {
      brainLink: "Mindful eating can be a good way for children to practice mindful awareness, and it may also promote healthy habits. Recent research has shown that even brief mindful eating interventions may lead to greater expectancy of liking different foods, greater enjoyment of all food, and lower caloric consumption of unhealthy foods.",
      gettingStarted: [
        "Remind the class about your agreed-upon ways of being together to create a safe, kind, and focused learning community.",
        "Invite a brief class discussion about the previous sense lesson, Mindful Smelling. Ask if anyone would like to share their experiences with the MindUP for Home and Life activity."
      ],
      activities: [
        {
          step: 1,
          title: "Mindful Tasting",
          instructions: [
            "Ask students to sit comfortably in a circle.",
            "Explain that everyone will have a chance to explore an object using mindful seeing, smelling, and tasting.",
            "Give each student a raisin or other small piece of food (being aware of choosing healthy options that avoid food allergies) in their hand, and ask them to simply hold it (palm open) and wait for further instruction.",
            "Begin with mindful seeing: Look very closely at your object, as if you have never seen it before. What does it look like? What color(s) do you see? What shape is it?",
            "Next, guide students through mindful smelling: Now, bring your object to your nose, and mindfully smell your object. Have you smelled this scent before? Does it smell sweet or sour? Does the smell remind you of anything?",
            "Finally, guide students through mindful tasting: Next, bring your object to your lips. Gently place your object in your mouth, but do not bite down. Bring your attention fully to the sensation of the object in your mouth. Now, move the object to the back of your mouth, in between your back molar teeth. Hold it there for a moment. Now, bite down on your object. What sensations are you experiencing? Taste? Texture? Now, slowly and mindfully, eat your objects!"
          ]
        }
      ],
      makingConnections: [
        "By paying attention the best way we can to our present experience, we can experience things more fully.",
        "Mindful tasting is another way we can use our senses to be mindful and practice focusing our attention.",
        "Mindful eating helps us slow down our eating so we can recognize when we're full. It also helps us enjoy the taste of our food more.",
        "There are physical benefits to eating mindfully. For example, when we chew our food more carefully and fully, we allow our digestive enzymes to break down our food more completely to turn it into energy to fuel us!",
        "Did you know that it takes a full 10 minutes for our brain to receive the message from our stomach that we are full? If we eat unmindfully, or too quickly, we don't have awareness of our bodies. This can lead to overeating.",
        "Invite students to consider how mindful tasting can contribute to a safe, kind, and focused learning community."
      ],
      followingUp: {
        mindupForHomeAndLife: [
          "As a class, brainstorm ways in which mindful tasting might apply to students' daily living.",
          "Bring your attention to the tastes you experience at different times during your day: Try to really taste and enjoy your food by eating it slowly and mindfully. What do you notice? Smell the food you are about to eat. Then try plugging your nose and see how the food tastes.",
          "Pay attention to these tastes when you eat throughout your day. If you are ready to reflect on your experiences, record your discoveries in a journal, in your sketchbook, on an iPad, etc., to refer to later for the Mindful Me Portfolio entry."
        ],
        mindfulMePortfolio: "Invite students to create a portfolio entry about their experience with the MindUP for Home and Life topic, mindful tasting. This entry could take the form of a picture, poem, song, paragraph, photograph, etc.—the sky's the limit!"
      },
      simplificationIdeas: [
        "Chart favorite foods that students enjoy tasting, and discuss what they love about them.",
        "Chart least favorite foods, and discuss why they don't enjoy them.",
        "Make simple class recipes together, and mindfully taste the food together.",
        "Invite a guest chef in to do a simple cooking activity with the class, followed by mindful tasting."
      ],
      challengerIdeas: [
        "Introduce exotic/uncommon foods for students to mindfully taste.",
        "Chart descriptive words related to the unique foods they taste.",
        "Practice mindful tasting in the lunchroom.",
        "Use mindful tasting as an opportunity to teach simple nutrition and healthy eating choices.",
        "Grow vegetables in a school garden. Harvest them, and mindfully taste what you have grown."
      ],
      literatureList: [
        "Fry Bread – Kevin Noble Maillard",
        "No Ordinary Apple – Sara Marlowe",
        "Gregory, the Terrible Eater – Mitchell Sharmat",
        "Eating the Alphabet – Lois Elhert",
        "The Sandwich Swap – Queen Rania of Jordan",
        "Pumpkin Soup – Helen Cooper",
        "If Snowflakes Tasted Like Fruitcake – Stacey Previn"
      ]
    }
  },
  // Lesson 9: Mindful Touch
  {
    id: "k2-u2-l9",
    unit: 2,
    unitTitle: "Mindful Senses",
    lessonNumber: 9,
    title: "Mindful Touch",
    purpose: "Students practice being mindful by using the sense of touch to focus their attention on external items.",
    concepts: [
      "Mindful touch helps me practice focusing my attention.",
      "I can use mindful touch as a Brain Break."
    ],
    objectives: [
      "Practice focusing their attention with the sense of touch.",
      "Describe how mindful touch differs from typical touch experiences."
    ],
    socialEmotionalLearning: [
      "Self-awareness",
      "Social awareness",
      "Self-management"
    ],
    materials: [
      "Different natural elements to touch (leaves, rocks, sand, cotton, linen)",
      "Different 'manufactured' elements to touch (plastic, polyester, glass, paper)",
      "Large coffee can with a sock attached to the opening OR a cloth bag (opaque)",
      "Drawing book or individual drawing paper",
      "Mindful Me Portfolio"
    ],
    estimatedTime: "30–35 minutes",
    content: {
      brainLink: "Executive functions (i.e., working memory, response inhibition, and cognitive flexibility) are some of the main functions of our prefrontal cortex (PFC). When children focus attention on the sensation of touch, they activate the brain's alerting and focusing pathways. The repeated stimulation of these pathways makes the neural circuits stronger and increases their ability to actively direct their attention where it is needed.",
      gettingStarted: [
        "Remind the class about your agreed-upon ways of being together to create a safe, kind, and focused learning community.",
        "Invite a brief class discussion about the previous sense lesson, Mindful Tasting. Ask if anyone would like to share their experiences with the MindUP for Home and Life activities.",
        "Review the mindful sensing activities the class has practiced so far: Mindful listening, Mindful seeing, Mindful smelling, Mindful tasting. Ask students if they can identify the last sense they have left to practice. Answer: Touch!"
      ],
      activities: [
        {
          step: 1,
          title: "Practice Mindful Touch",
          instructions: [
            "Ask students to gather in a circle. Invite them to participate in the mindful activity.",
            "Ask students to bring their hands together, and give the following instructions: Bring the palms of your hands together. Bring all of your attention to your hands. Perhaps rub your fingers together. Notice how your fingers touch one another and the sensation that you feel.",
            "Now, take a moment and notice the temperature of your hands. Are they cool or warm?",
            "Bring your hands up to touch your cheeks. Are your hands warm or cool?",
            "Now, bring your hands back together. Begin to rub your hands together very quickly. Notice the feeling of your hands moving together. Are they getting warmer?",
            "Now, place your hands to your cheeks again. What do you notice? Are they warmer?"
          ]
        },
        {
          step: 2,
          title: "Mystery Objects",
          instructions: [
            "Introduce the mystery objects activity: That was a simple way to help us pay attention in a mindful way. Our sense of touch is always with us, and we can use it to take a Brain Break whenever we need.",
            "Read the script: I am going to pass around a container containing a mystery object. When you reach your hand into the container, please keep your thoughts to yourself—we will share our thoughts when everyone has mindfully touched the object.",
            "Don't try to peek! Just use your sense of touch to explore this object. When you reach into the container, take a moment to really feel it. Like a scientist, you want to investigate this object with curiosity. Notice what the texture is: smooth, rough, sharp, soft, etc.",
            "Focus all of your attention on each object you hold. When you are done touching the mystery object, silently pass it to the person next to you."
          ]
        }
      ],
      makingConnections: [
        "Mindful touch allows you to enjoy the full experience of touching something.",
        "It's another way to practice focusing your attention.",
        "You can use mindful touch as a Brain Break.",
        "Invite students to consider how mindful touch can contribute to a safe, kind, and focused learning community."
      ],
      followingUp: {
        mindupForHomeAndLife: [
          "As a class, brainstorm ways in which mindful touch might apply to students' daily living.",
          "Bring your attention to all the times you touch/feel things during your day: Try really feeling the clothes you wear. Do you have a favorite sweater you love to put on? How does it feel against your skin?",
          "If there is a garden in your neighborhood, spend time exploring what is growing there. Gently feel the parts of the plants. What do you feel?",
          "If there is a green space or a beach in your neighborhood, try feeling the grass or sand beneath your bare feet. Really feel the way the grass/sand moves against your skin.",
          "Pay attention to what you feel and touch when you move through your day. If you are ready to reflect on your experiences, record your discoveries in a journal, in your sketchbook, etc., to use when creating your Mindful Me Portfolio entry for this lesson."
        ],
        mindfulMePortfolio: "Invite students to create a portfolio entry about their experience with the MindUP for Home and Life topic, mindful touch. This entry could take the form of a picture, poem, song, paragraph, photograph, etc.—the sky's the limit!"
      },
      simplificationIdeas: [
        "Take mindful touching outside. Have students mindfully touch things on the playground, the trees, the grass, etc.",
        "While instructing students on best handwashing strategies, draw their attention to the way the water, soap, dryer, or paper towel feels and how their hands feel when they are clean.",
        "Have students remove their shoes in the classroom and experience walking mindfully around the room, drawing their attention to their feet on the floor.",
        "Invite students to use their fingers to make letters and numbers with whipped cream, shaving cream, or finger paint."
      ],
      challengerIdeas: [
        "Take students outside, and invite them to sit in their own space. Ask them to notice the weather and how it feels on their bodies.",
        "Engage students in a sculpting activity using a variety of materials, such as tinfoil, clay, plasticine, pipe cleaners, etc. Allow them to use these materials to create whatever their imaginations allow. As they create, ask them to focus on what they are touching and how it feels."
      ],
      literatureList: [
        "Don't Touch My Hair – Sharee Miller",
        "I Hear a Pickle – Rachel Isadora",
        "The Girl Who Heard Colors – Marie Harris",
        "The Black Book of Colors – Menena Cottin",
        "Hello, Ocean – Pam Muñoz Ryan",
        "I Touch – George Patrick"
      ]
    }
  },
  // Lesson 10: Mindful Movement
  {
    id: "k2-u2-l10",
    unit: 2,
    unitTitle: "Mindful Senses",
    lessonNumber: 10,
    title: "Mindful Movement",
    purpose: "Students practice being mindful by focusing their attention on internal bodily sensations.",
    concepts: [
      "Mindful movement helps me practice focusing my attention.",
      "Sensations in my body are information about how I'm feeling."
    ],
    objectives: [
      "Practice focusing their attention on internal bodily sensations.",
      "Compare sensations in their bodies in a relaxed state to sensations in an excited state."
    ],
    socialEmotionalLearning: [
      "Self-awareness",
      "Self-management"
    ],
    materials: [
      "Chart paper",
      "Mindful Me Portfolio"
    ],
    estimatedTime: "20–25 minutes",
    content: {
      brainLink: "In this lesson, students observe their heart rate and breathing to see how they can bring full awareness to their body and their ability to self-regulate by using their breath to relax their body to bring their heart rate down. Stress can result in increased cortisol release. With increased awareness of their ability to regulate their bodies and their emotional responses, students can learn to react differently to stress.",
      gettingStarted: [
        "Remind the class about your agreed-upon ways of being together to create a safe, kind, and focused learning community.",
        "Invite a brief class discussion about the previous lesson, Mindful Touch. Ask if anyone would like to share their experiences with mindful touch in their daily lives."
      ],
      activities: [
        {
          step: 1,
          title: "Relax, Jump, and Relax",
          instructions: [
            "Ask students to sit comfortably in a circle. First, demonstrate how and where they will need to put their hand in order to feel their heartbeat or their pulse (chest or wrist).",
            "Lead students through the following script: Sitting comfortably, take notice of the way your body feels. Bring all your attention to your body and to your breath. Take several deep, slow breaths. If you have thoughts or feelings, notice them, and then gently bring your attention back to your body.",
            "Now, place your hand on your heart or your fingers to your wrist to feel your pulse. Take a few moments to be fully aware of your body in this relaxed state.",
            "Next, ask students to get up and jump in one place for 1 to 2 minutes.",
            "After the jumping activity, ask students to sit back down, and offer the following instructions: Sitting comfortably, feel your pulse with your hand on your heart or fingers on your wrist. Is it faster now than before the jumping activity? This is your body in an excited state.",
            "Now, I want you to try to return to that relaxed state. Keep feeling your heartbeat. Focus your attention on calming your body. Notice your breath. Is it fast or slow? Try to take deep, slow breaths. Try your best to stay focused on your body. If you have thoughts or feelings, notice them, and then gently bring your attention back to your body. Try to feel your body in that relaxed state."
          ]
        }
      ],
      makingConnections: [
        "Mindful movement is another way we can use our senses to be mindful and practice focusing our attention.",
        "It can help us notice sensations in our bodies. These sensations are information about how we're feeling.",
        "It's okay for your body to feel excited! But, it's important to know how to calm your body when you need to.",
        "By paying attention the best way we can to our bodies, we can better understand ourselves. Our bodies are giving us important signals all the time, and if we listen carefully, we can choose actions to benefit our bodies and minds.",
        "Invite students to consider how mindful movement can contribute to a safe, kind, and focused learning community."
      ],
      followingUp: {
        mindupForHomeAndLife: [
          "As a class, brainstorm ways in which mindful movement might apply to students' daily lives.",
          "Bring your attention to the way your body moves at different times during your day: How does your body feel first thing in the morning after a long sleep? How do you move when you first get out of bed?",
          "Notice how you move on your way to and from school. Are you walking or running? How does your backpack feel? Heavy? Light?",
          "Sit down and feel your pulse when your body is calm. Now jump, run, kick, or shake for a few minutes. Check your pulse again. What do you notice?",
          "Pay attention to the way your body moves when you are in these settings. If you are ready to reflect on your experiences, record your discoveries in a journal, in your sketchbook, etc."
        ],
        mindfulMePortfolio: "Invite students to create a portfolio entry about their experience with the MindUP for Home and Life topic, mindful movement. This entry could take the form of a picture, poem, song, paragraph, photograph, etc.—the sky's the limit!"
      },
      simplificationIdeas: [
        "Engage the students in brief balancing activities throughout the day. Encourage them to stand up and focus on something on the wall that doesn't move, and then practice balancing on one foot and then the other.",
        "Engage students in simple body breaks throughout the day—for example, hopping, shaking, stretching, etc.",
        "Have students do simple yoga postures using books such as 'The Yoga Game' or simple yoga posture cards.",
        "Play music, and have students move to the beat.",
        "Focus on mindfully moving during transitions throughout the day. This could be simply lining up in their own space quietly and slowly, eyes forward, no talking, etc."
      ],
      challengerIdeas: [
        "Play the 'Mirror Game,' where students sit in pairs facing each other and mirror what the other student does.",
        "Have students creatively dance to a variety of music styles, moving their bodies to match the music (fast, slow, jerky, smooth, etc.).",
        "Introduce a weekly yoga practice by bringing in a local yoga instructor.",
        "Use the lessons/activities taught in the gym to draw attention to moving mindfully."
      ],
      literatureList: [
        "Mindful Movements: Ten Exercises for Well-Being – Thich Nhat Hanh",
        "The Yoga Game – Kathy Beliveau",
        "Karate Hour – Carol Nevius",
        "Run Wild – David Covell",
        "I Am Yoga – Susan Verde"
      ]
    }
  },
  // Unit 3: Building Well-Being with a Mindful Mindset
  // Lesson 11: Exploring Feelings
  {
    id: "k2-u3-l11",
    unit: 3,
    unitTitle: "Building Well-Being with a Mindful Mindset",
    lessonNumber: 11,
    title: "Exploring Feelings",
    purpose: "Students will develop important emotional literacy skills by learning to notice and name their own feelings and to manage strong feelings.",
    concepts: [
      "The more feelings words I know, the easier it is to name my feelings.",
      "Sensations in my body are clues about how I'm feeling.",
      "I can manage strong feelings with mindful breathing."
    ],
    objectives: [
      "Identify a variety of feelings in response to scenarios.",
      "Connect sensations in their bodies to different feelings.",
      "Recognize that uncomfortable sensations signal strong feelings."
    ],
    socialEmotionalLearning: [
      "Self-awareness",
      "Self-management"
    ],
    materials: [
      "Chart paper",
      "Markers",
      "Optional: Chime"
    ],
    estimatedTime: "30 minutes",
    content: {
      brainLink: "In this lesson, students learn key skills that contribute to emotional literacy: noticing and naming feelings, and ways to manage strong feelings. Emotions start as sensations in the body before we are even aware that we are having a feeling. When students practice recognizing these sensations, they gain an awareness of their own physical cues and are better able to notice when they are having different feelings. Emotional granularity—the ability to put feelings into precise, specific words—is also essential to emotional literacy. Naming strong feelings is also a recognized way to manage strong emotions. It is a cognitive task that engages the prefrontal cortex (PFC). Research shows that naming an angry feeling reduces activity in the amygdala.",
      gettingStarted: [
        "Please remember our strategies to be safe, kind, and focused.",
        "Briefly review Unit 2: Mindful Senses lessons.",
        "Connect your students' prior learning to today's topic: You've been practicing being mindful by focusing your attention on sounds, sights, flavors, your breathing, and more. Today, you're going to practice being mindful about your feelings. Everyone has feelings, and all feelings are okay."
      ],
      activities: [
        {
          step: 1,
          title: "Learning Feelings Words",
          instructions: [
            "Ask students what they know about feelings: What are feelings? What are some examples of feelings?",
            "We all have many feelings. We can be happy in one moment and sad in the next. Our feelings change throughout the day.",
            "Invite students to notice and name their feelings: Notice how you're feeling right now. What's the best word for your feeling? Finding the right name for a feeling can be hard. Let's think of feelings words together.",
            "Make a list of feelings words on chart paper. Keep the list posted for easy reference.",
            "Have students name their feelings in different situations. Help them use the list of feelings words to name their feelings.",
            "Read the script: How do you feel when: you're having fun with a friend? you're going somewhere new? you've fallen and hurt yourself? someone takes your toy? you help someone with a chore?",
            "We all have feelings. And all feelings are okay! The more feelings words we know, the easier it is to name our feelings."
          ]
        },
        {
          step: 2,
          title: "Sensing Feelings in Our Bodies",
          instructions: [
            "Connect mindfulness to noticing sensations in the body: Being mindful helps you notice things with your senses, like sounds, smells, tastes, and more. You can also be mindful to notice sensations in your body. These sensations are clues about how you feel.",
            "Tell students they're going to practice noticing sensations in their body by closing their eyes and focusing their attention on what they sense inside their body.",
            "Ask students to sit comfortably and to close their eyes if they want to. Have them focus their attention on what's going on inside their bodies—their stomach grumbling, heart beating, muscles twitching, and so on.",
            "After 30 seconds, ask students to open their eyes and share the sensations they noticed in their bodies. Record the sensations they name on chart paper.",
            "Connect noticing sensations to naming feelings: Sensations in your body are clues about how you feel. Noticing these sensations can help you name your feelings.",
            "Think about a time you felt really happy. Point to where you felt that in your body. Continue this activity with other feelings.",
            "Explain to students that strong sensations signal strong feelings: Sometimes the sensations in our body are uncomfortable. That's a clue you're feeling a strong feeling. Strong sensations and strong feelings tell our guard dog (amygdala) to get ready to react!",
            "Ask students what they can do to calm down their amygdala: You've already learned one way to calm down your amygdala. What is it? Mindful breathing is one way you can calm down your amygdala and manage strong feelings."
          ]
        }
      ],
      makingConnections: [
        "Today we talked about noticing and naming feelings. Everyone has feelings, and all feelings are okay.",
        "Noticing sensations in our bodies give us clues about what we're feeling.",
        "Uncomfortable sensations mean we're having a strong feeling.",
        "You can use our list of feelings words to help you name your feelings. And you can use mindful breathing to help you calm down strong feelings.",
        "Invite students to consider how noticing and naming our feelings can help our learning community be safe, kind, and focused."
      ],
      followingUp: {
        mindupForHomeAndLife: [
          "Have students keep a simple feelings diary for one weekend. Have them record how they're feeling at different times of the day and what is happening at the time.",
          "Do they feel the same way in the same situation each day? Do their feelings change during the day? Do they have any strong feelings? What do they do to calm them down?"
        ],
        mindfulMePortfolio: "Have students keep a feelings diary at school. Students can choose how they represent what they are feeling at different points in the day. After one week of logging their feelings, have students reflect on any patterns they notice."
      },
      simplificationIdeas: [
        "On a whiteboard or chart paper, draw a simple outline of a gingerbread person. Have students circle or color in where they experience sensations in their body.",
        "As a group, do a feelings check-in at different points during the day.",
        "Play feelings charades. Have a student choose a card with a labeled image of a feeling and act it out for classmates.",
        "Have students draw how they're feeling."
      ],
      challengerIdeas: [
        "Give students a feelings word, such as 'happy,' and ask them to list as many synonyms as they can.",
        "Use children's literature, such as 'Anh's Anger' or 'The Recess Queen,' to explore feelings and strategies to manage them.",
        "Have students create a feelings dictionary in which they can define, draw, and label emotions.",
        "Have students think of ways they can manage strong feelings—for example, taking a movement break, coloring, asking for help, or listening to music."
      ],
      literatureList: [
        "The Feelings Book – Todd Parr",
        "When Sophie Gets Angry—Really, Really Angry – Molly Bang",
        "When Sophie's Feelings Are Really, Really Hurt – Molly Bang",
        "The Way I Feel – Janan Cain",
        "Jabari Jumps – Gaia Cornwall",
        "In My Heart: A Book of Feelings – Jo Witek",
        "The Color Monster: A Story about Emotions – Anna Llenas",
        "Grumpy Monkey – Suzanne Lang",
        "The Rabbit Listened – Cory Doerrfeld",
        "When Sadness Is at Your Door – Eva Eland",
        "Ruby Finds a Worry – Tom Percival",
        "When You Are Brave – P. Miller",
        "Sometimes I'm a Bombaloo – Rachel Vail",
        "My Blue Is Happy – Jessica Young",
        "Anh's Anger - Gail Silver",
        "The Recess Queen - Alexis O-Neill"
      ]
    }
  },
  // Lesson 12: Building Perspective-Taking and Empathy
  {
    id: "k2-u3-l12",
    unit: 3,
    unitTitle: "Building Well-Being with a Mindful Mindset",
    lessonNumber: 12,
    title: "Building Perspective-Taking and Empathy",
    purpose: "Students practice perspective-taking by identifying the perspectives of characters in a story and connecting situations in the story to their own lives.",
    concepts: [
      "Perspective-taking means being mindful of what others might be thinking or feeling.",
      "Perspective-taking helps me understand how others might be feeling.",
      "Understanding how others might be feeling is part of empathy."
    ],
    objectives: [
      "Explain how the perspectives of characters in a story differ.",
      "Explain how taking others' perspectives can help them better understand others."
    ],
    socialEmotionalLearning: [
      "Self-awareness",
      "Self-management",
      "Social awareness",
      "Relationship skills",
      "Responsible decision-making"
    ],
    materials: [
      "Fable or fairy tale",
      "Chart paper",
      "Mindful Me Portfolio"
    ],
    estimatedTime: "20–25 minutes",
    content: {
      brainLink: "Perspective-taking skills can help positively guide students' behavior by enabling them to accurately interpret cues in their environment reflectively instead of reactively. Students can use perspective-taking to interpret their experiences and others' behaviors more thoughtfully, which enables them to respond with empathy. Practicing perspective-taking engages students' Wise Owl (PFC) to build neural networks that quell anxiety, improve self-regulation, and facilitate ethical decision-making in their social and academic lives.",
      gettingStarted: [
        "Remind your students of the attitudes and actions agreed upon together to ensure the class is safe, kind, and focused.",
        "Ask students to recall the previous lesson and the key learning about understanding emotions: The more feelings words I know, the easier it is to name my feelings. Sensations in my body are clues about how I'm feeling. I can manage strong feelings with mindful breathing.",
        "Invite students to share their experiences with the MindUP for Home and Life activity."
      ],
      activities: [
        {
          step: 1,
          title: "Discussion of Characters' Perspectives",
          instructions: [
            "Select a well-known fable or fairy tale that's relevant for your students. 'Goldilocks and the Three Bears' is one widely available story that works well with this lesson.",
            "Read and/or review the story with your class. On a chart, write the names of the main characters in the story.",
            "Have students consider the perspectives of the different characters by asking questions: What do you think Goldilocks was thinking and feeling when she saw the empty house? Why do you think she acted the way she did when inside the house? Do you think she thought she was doing something wrong?",
            "What do you think the bears were thinking and feeling when they saw the house and what had happened inside? Do you think the bears may have done something wrong? (leaving the door open, etc.)",
            "Is it possible that both Goldilocks and the bears did the right thing? Did you notice any example of mindfulness in any of the characters, such as mindful listening?"
          ]
        },
        {
          step: 2,
          title: "Introduce Perspective-Taking and Empathy",
          instructions: [
            "Read the script: Today, we practiced taking the perspective of another person. Perspective-taking is being mindful of others and what they might be thinking or feeling.",
            "Perspective-taking helps us have empathy for another person. Having empathy means feeling or understanding what another person might be feeling (like happiness, sadness, anger, or excitement) in a situation.",
            "Being able to take someone else's perspective—to put yourself in their shoes—is necessary for having empathy.",
            "What do you think might have happened if our characters had shown more empathy to one another?"
          ]
        }
      ],
      makingConnections: [
        "Thinking about how others might think and feel is an important way we can understand others.",
        "By understanding others and having empathy for others, we are able to build and maintain good relationships.",
        "Sometimes your perspective might be different from someone else's. Taking time to understand others' perspectives before you act is acting mindfully.",
        "Invite students to consider how thinking about other people's perspectives can help our learning community be safe, kind, and focused."
      ],
      followingUp: {
        mindupForHomeAndLife: [
          "As a class, brainstorm ways in which perspective-taking might apply to students' daily lives.",
          "Bring your attention to the way you connect with friends, family, and adults during your day. When you look at a person's face, can you tell what they might be feeling? Do they look like they might be happy or sad?",
          "Try asking someone how they are feeling and really listening to what they tell you. Listen with your whole body—focus with your eyes and ears, and turn your body to face them. Notice if you feel what they are feeling.",
          "Consider the characters in a book you are reading. Think about what it would be like to live and experience what they do.",
          "If you are ready to reflect on your experiences, record your discoveries in a journal, in your sketchbook, etc., to be used when creating your Mindful Me Portfolio entry."
        ],
        mindfulMePortfolio: "Invite students to create a portfolio entry about their experience with the MindUP for Home and Life topics perspective-taking and empathy. This entry could take the form of a picture, poem, song, paragraph, photograph, etc.—the sky's the limit!"
      },
      simplificationIdeas: [
        "Introduce the cover of the book 'Duck! Rabbit!' to the students, or draw the image on a large chart paper. Ask students what they see. Is it a duck or a rabbit? Help them understand that we don't all see the same thing the same way. People often have a variety of perspectives on the same thing.",
        "Read the book 'Duck! Rabbit!' and lead students in a discussion about each page."
      ],
      challengerIdeas: [
        "Read the book 'Hey, Little Ant,' from the MindUP literature list and at the end of the story, discuss both the ant's and the boy's perspectives. Ask, 'What should the boy do?' Explore students' answers and the rationale behind them, reinforcing the idea that we often see things from different perspectives.",
        "Find a variety of ambiguous art images for students to view. Engage in a discussion around what they see and how that may differ from what their friend sees.",
        "Take students outside to look at the clouds, and discuss what each student sees—the same cloud looks like something different to each person."
      ],
      literatureList: [
        "Hey, Little Ant – Phillip and Hannah Hoose",
        "Time for Bed's Story – Monica Arnaldo",
        "Duck! Rabbit! – Amy Krouse Rosenthal & Tom Lichtenheld",
        "The Cool Bean – Jory John & Pete Oswald",
        "Bear and Bee – Sergio Ruzzier",
        "The Skin You Live In – Michael Tyler",
        "Whimsy's Heavy Things – Julie Kraulis",
        "Edwardo: The Horriblest Boy in the Whole Wide World – John Burningham",
        "Wemberly Worried – Kevin Henkes",
        "One – Kathryn Otoshi",
        "The Recess Queen – Alexis O'Neill",
        "Spoon – Amy Krouse Rosenthal",
        "I Am Jazz! – Jessica Herthel",
        "Hey, Dog – Tony Johnson",
        "The Book of Mistakes – Corinna Luyken",
        "What Do You Do with an Idea? – Kobi Yamada",
        "What Do You Do with a Problem? – Kobi Yamada"
      ]
    }
  },
  // Lesson 13: Practicing Optimism
  {
    id: "k2-u3-l13",
    unit: 3,
    unitTitle: "Building Well-Being with a Mindful Mindset",
    lessonNumber: 13,
    title: "Practicing Optimism",
    purpose: "Students practice perspective-taking by identifying how optimistic and pessimistic views of a problem differ. They also consider how they can choose to view things with more optimism in their own lives.",
    concepts: [
      "Choosing to view things with optimism can make me happier.",
      "Considering alternative explanations for a problem can help me change negative thoughts and feelings to positive ones."
    ],
    objectives: [
      "Distinguish between viewing a problem with optimism and with pessimism.",
      "Practice strategies to help them approach life with more optimism."
    ],
    socialEmotionalLearning: [
      "Self-awareness",
      "Self-management",
      "Social awareness"
    ],
    materials: [
      "Glass half full of water",
      "Chart paper"
    ],
    estimatedTime: "20–25 minutes",
    content: {
      brainLink: "Our brain is wired with a negativity bias, which means we tend to view and make sense of the world using negative information far more than positive information. The good news is that we can help them train their brains to view the world with a more positive lens. Neurons that fire together wire together! Repetition strengthens neural pathways, just as exercise grows muscles. When children practice imagining positive future events, they increase their brain's tendency toward optimism, and this optimism builds resilience and perseverance. With practice, they can make optimism their default setting and create a habit of positivity.",
      gettingStarted: [
        "Remind the class about your agreed upon ways of being together to create a safe, kind, and focused.",
        "Review learning around perspective-taking and empathy: Why are perspective-taking and empathy important? They help us be mindful of others. Perspective-taking allows us to understand other points of view. Empathy helps us understand how others feel."
      ],
      activities: [
        {
          step: 1,
          title: "Introducing Optimistic and Pessimistic Points of View",
          instructions: [
            "Display a glass half filled with water, and ask students what they see: Is the glass half empty or half full?",
            "Why do you think some people see it as half empty while others see it as half full?",
            "Define optimist and pessimist: An optimist is someone who thinks positively, or takes a hopeful point of view. A pessimist is someone who thinks negatively, or does not take a hopeful point of view.",
            "When you take an optimistic view, you think things like: Things will work out. I can do this! Something good might happen!",
            "When you take a pessimistic view, you think things like: Things won't work out. I can't do this. I think something bad will happen."
          ]
        },
        {
          step: 2,
          title: "Approaching a Problem: A Pessimistic Point of View",
          instructions: [
            "Ask students to consider a common problem. For example: We had plans to play outside today, and it is raining.",
            "Guide a group discussion, and brainstorm a pessimistic point of view on this problem: What would you believe about this problem if you were looking at it from a pessimistic view?",
            "What are your thoughts? Examples: 'This always happens when we make plans to play outside.' 'Our day is ruined.'",
            "What are your feelings? Example: 'I'm sad and disappointed.'"
          ]
        },
        {
          step: 3,
          title: "Considering Alternative Explanations: An Optimistic Point of View",
          instructions: [
            "Now, instruct students to consider another perspective—an optimistic point of view.",
            "Guide a discussion in which students brainstorm an optimistic view of the same problem: What would you believe about this problem if you were looking at it from an optimistic view?",
            "Example: 'It's raining, so let's get our rain gear on and head out to play in the puddles.'",
            "What are your thoughts? Example: 'It's fun to take a walk in the rain and play in puddles. Let's see how many puddles we can find!'",
            "What are your feelings? Example: 'I'm excited to splash in the puddles!'"
          ]
        }
      ],
      makingConnections: [
        "There are two ways to approach challenges—with optimism or with pessimism.",
        "We know that people who view things optimistically tend to be happier.",
        "The good news is that we can choose to take an optimistic view!",
        "Invite students to consider how practicing optimism can help our learning community be safe, kind, and focused."
      ],
      followingUp: {
        mindupForHomeAndLife: [
          "As a class, brainstorm ways in which practicing optimism can apply to students' daily lives.",
          "Bring your attention to the way you connect with friends, family, and adults during your day.",
          "If you are playing a game and you or your team are not winning, try looking at it from another perspective. Try seeing it as an opportunity to play with your friends. Think about how lucky you are to be able to play and have fun.",
          "If the weather outside is not what you expected, try to find an optimistic way to look at it. Find activities that are fun no matter what the weather."
        ],
        mindfulMePortfolio: "Ask students to draw how they feel when they look at the glass as half full. Students can brainstorm optimistic/pessimistic beliefs, thoughts, and feelings as an entry in their Mindful Me Portfolio. This entry could take the form of a picture, poem, song, paragraph, photograph, etc.—the sky's the limit!"
      },
      simplificationIdeas: [
        "Read 'Pete the Cat and His Magic Sunglasses.' Discuss what happens when Pete puts his magic glasses on, drawing attention to the fact that Pete doesn't actually need them to see things in an optimistic way.",
        "Create paper glasses or toilet roll binoculars. Encourage students to go for a walk around the classroom, school, or outside and practice 'seeing' the positive or the good in what is happening around them.",
        "As a group, chart the many positive things that have happened during the day at school."
      ],
      challengerIdeas: [
        "Engage the students in a discussion around the phrase 'I can't do it' and what might motivate them to say this phrase. What might be holding them back from attempting the task? What is the problem? Guide students to reframe the way they look at the problem they are experiencing. Brainstorm positive language they could use instead, such as: I need help, please. I think I need to think about this. I think I will learn this one day. Maybe I can try it a different way. I need more time. I'll keep trying. I need more practice. I'll let this go for today. I tried my best, and that's all I can do."
      ],
      literatureList: [
        "Scaredy Squirrel – Melanie Watt",
        "The King of Kindergarten – Derrick Barnes",
        "Last Stop on Market Street – Matt de la Peña",
        "Pete the Cat and His Magic Sunglasses – Eric Litwin",
        "Pete the Cat: I Love My White Shoes – Eric Litwin",
        "Sunny – Celia Krampien",
        "I Dreamt a Book about Hope – Gabriela Olmos",
        "This Plus That: Life's Little Equations – Amy Krouse Rosenthal",
        "Wemberly Worried – Kevin Henkes"
      ]
    }
  },
  // Lesson 14: Thinking about Happy Experiences
  {
    id: "k2-u3-l14",
    unit: 3,
    unitTitle: "Building Well-Being with a Mindful Mindset",
    lessonNumber: 14,
    title: "Thinking about Happy Experiences",
    purpose: "Students practice thinking of a happy experience to boost their mood.",
    concepts: [
      "Thinking about a happy experience can help me change my mood."
    ],
    objectives: [
      "Describe thoughts and feelings they associate with a happy experience.",
      "Practice thinking of a happy experience.",
      "Describe thoughts and feelings they experience when thinking about a happy experience."
    ],
    socialEmotionalLearning: [
      "Self-awareness",
      "Self-management"
    ],
    materials: [
      "Chart paper",
      "Optional: Chime",
      "Mindful Me Portfolio"
    ],
    estimatedTime: "20–25 minutes",
    content: {
      brainLink: "In this lesson, students consider the thoughts and feelings associated with happy experiences and with remembering those experiences. Research shows that people who think about happy experiences they have had in the past can boost their level of happiness in the present. Remembering a positive experience stimulates the same neural networks as the experience itself and triggers the release of dopamine. Dopamine is a neurotransmitter associated with pleasure, attention, motivation, and perseverance.",
      gettingStarted: [
        "Remind students about your agreed-upon ways of being together to create a safe, kind, and focused classroom.",
        "Briefly review Lesson 13: Practicing Optimism: We know that people who choose an optimistic view tend to be happier in their lives. We can choose to be more optimistic by choosing to think with an optimistic point of view!",
        "Did anyone look at a problem differently? Did you try to look at it like the glass was half full and not half empty? (optimistic instead of pessimistic point of view)"
      ],
      activities: [
        {
          step: 1,
          title: "Introducing a Universal Happy Experience",
          instructions: [
            "Gather the students, and explain that first they will think about a happy experience. Then they'll identify the thoughts, feelings, and body sensations they associate with that happy experience.",
            "Read the script: We are going to make 'movies in our minds.' Our movies are going to be about happy experiences. Think about a very short movie that only shows the important parts in a short amount of time.",
            "We will think about a happy experience and imagine the thoughts, feelings, and body sensations that we experience during the happy event."
          ]
        },
        {
          step: 2,
          title: "Revisiting a Happy Experience",
          instructions: [
            "Begin by modeling thinking about a happy experience for the class. Narrate your experience, visualizing a happy experience in your mind. Relate the thoughts, feelings, and sensations you experience.",
            "Invite the class to choose a happy experience they had as a class. Examples include playing a fun game as a class, having a class celebration, and going on a great field trip.",
            "Ask students to be seated comfortably and close their eyes or look down at their hands.",
            "Using the following script, guide your class through a visualization exercise about the happy experience (2 minutes). Begin the activity with one ring of the chime.",
            "Read the script: Try your best to focus on the happy experience, as if it has just happened. Bring all your attention to the experience. Make a mini-movie in your mind. What do you see in your happy movie? Notice your thoughts. Notice your feelings. Notice how your body feels. If your mind wanders, that's okay—just bring your focus back to the happy experience.",
            "Complete the happy visualization activity with one ring of the chime. Allow students 30 to 45 seconds to fully bring their awareness back to the classroom."
          ]
        }
      ],
      makingConnections: [
        "In our last few lessons, we have been learning skills to help us be more joyful in our lives.",
        "We practiced perspective-taking, which helps us get along with others.",
        "We learned how to think more positively.",
        "Now we have learned a strategy to boost our happiness.",
        "By thinking about a happy time we had in the past, we can boost our happiness in the present moment.",
        "This is something you can do whenever you want a mood boost. Simply think of a happy time, and imagine it as we did in our activity.",
        "Invite students to consider how thinking about happy experiences can help our learning community be safe, kind, and focused."
      ],
      followingUp: {
        mindupForHomeAndLife: [
          "As a class, brainstorm ways in which thinking about happy experiences can apply to students' daily lives.",
          "Bring your attention to the way you're feeling during your day. If you're not feeling happy, what could you think about or do to feel happier?",
          "Make a list or drawings of activities that bring you happiness, and keep it somewhere handy at home. Refer to it when you need a reminder about what you can do to help yourself feel happy.",
          "Create a Joy Box to keep at home that has 3 objects in it that remind you of happy memories when you see/hold them. Keep it in a safe spot, and pull it out when you need more joy in your day.",
          "Put on your favorite song and dance to the music!",
          "Do something nice for someone else, and see how it makes you feel! Often, being kind to others makes us happy, too!"
        ],
        mindfulMePortfolio: "Invite students to create a portfolio entry about their experience with the MindUP for Home and Life topic, happiness. This entry could take the form of a picture, poem, song, paragraph, photograph, etc.—the sky's the limit!"
      },
      simplificationIdeas: [
        "Give students the opportunity to draw three things that make them happy, and display their drawings around the room.",
        "Read Pete the Cat books by Eric Litwin (or any suggested books from the accompanying MindUP Literature List), and engage students in a discussion around the characters who make the choice to see things with a happy outlook.",
        "Play uplifting music/songs."
      ],
      challengerIdeas: [
        "Have students write about three happy experiences, and display their writing around the classroom.",
        "Have students create 'Joy Boxes' in which they place a few small items (a photo, a special rock, a feather, etc.) that bring them happiness. Consider having students keep the boxes in their desks for easy access at times when they need to be reminded of things that bring them joy."
      ],
      literatureList: [
        "If You're Happy and You Know It – Jane Cabrera",
        "Layla's Happiness – Mariahdessa Ekere Tallie",
        "Happy! – Pharrell Williams",
        "Have You Filled a Bucket Today? – Carol McCloud",
        "The Pig of Happiness – Edward Monkton",
        "My Heart Fills with Happiness – Monique Gray Smith"
      ]
    }
  },
  // Unit 4: Mindful of Ourselves in the World
  // Lesson 15: Practicing Gratitude
  {
    id: "k2-u4-l15",
    unit: 4,
    unitTitle: "Mindful of Ourselves in the World",
    lessonNumber: 15,
    title: "Practicing Gratitude",
    purpose: "Students practice gratitude to boost their happiness.",
    concepts: [
      "Practicing gratitude helps me be happier."
    ],
    objectives: [
      "Define gratitude.",
      "Practice gratitude through group activity and journaling.",
      "Recognize how practicing gratitude can boost happiness."
    ],
    socialEmotionalLearning: [
      "Self-awareness",
      "Self-management",
      "Social awareness"
    ],
    materials: [
      "Gratitude stone or crystal",
      "Gratitude journal or Mindful Me Portfolio",
      "Chart paper"
    ],
    estimatedTime: "30 minutes (over 2 days)",
    content: {
      brainLink: "Neuroscience research has revealed that students' comfort levels affect how well information is received and stored in the brain. When students learn that they have the ability to be aware of and manage their emotions, they are more resilient, optimistic, and motivated. Teaching students to develop habits of gratitude helps them with this emotional awareness and management. Gratitude journals provide students with a low-risk opportunity to build these habits by sharing what they're thankful for.",
      gettingStarted: [
        "Please remember our strategies to be safe, kind, and focused.",
        "Briefly review Lesson 14: Happy Experiences: Thinking about happy experiences we had in the past helps us feel happier in the present. This is something you can do whenever you want a mood boost.",
        "What happened when you remembered a happy time or used something from your Joy Box? How did you feel during the experience? What thoughts came up? How did your body feel?"
      ],
      activities: [
        {
          step: 1,
          title: "Define Gratitude",
          instructions: [
            "Introduce the lesson by asking the class about gratitude: Does anyone know what gratitude is?",
            "Define gratitude: Think of a time you felt truly thankful for something someone did for you or for something you have. That feeling is called gratitude. Gratitude means feeling thankful for someone or something.",
            "Check for understanding by asking students to state what gratitude means to them."
          ]
        },
        {
          step: 2,
          title: "Group Gratitude Activity",
          instructions: [
            "Ask students to sit in a circle. You may do this activity in your classroom or take the class outside and sit in a circle in a green space.",
            "Explain the activity to students: For this activity, we are each going to think of one thing we are grateful for today. It could be something somebody did for you, an event that happened, or something you noticed that you appreciated (for example, the flowers on a tree).",
            "We are going to pass around this special stone (or crystal), and when you receive the stone, you can say what you are grateful for and then pass it to your neighbor.",
            "If you are not comfortable sharing your idea, you can say 'pass.' You can also hold the stone and think about what you are grateful for.",
            "Sometimes, if we are feeling sad, upset, or angry, it is more difficult to think of what we are thankful for. This is okay. Just take some time and notice where you are, and see if you can think of one small thing you are thankful for (for example, recess). If you can't think of anything, that's okay, too.",
            "Please remember that the only one speaking is the person holding the gratitude stone.",
            "Begin passing the stone around the circle. After the exercise, allow a few minutes for discussion and comments about the activity, if they arise."
          ]
        },
        {
          step: 3,
          title: "Instructions for Gratitude Journal",
          instructions: [
            "Introduce the gratitude journal to your class: We just experienced practicing gratitude. It helped us focus on the positive things in our lives.",
            "Sometimes, we are so busy in our lives that we don't take the time to think about the things that we are thankful for.",
            "So we are going to try an experiment and see what happens if we take some time each day to think about things we are thankful for.",
            "Every day, you will share one thing you are grateful for and illustrate it in a gratitude journal. You can use a new notebook or create entries in your Mindful Me Portfolio.",
            "After five days, we will review our experiment and talk about your experience practicing gratitude."
          ]
        }
      ],
      makingConnections: [
        "Sharing Experiences: Invite students to share their experiences and thoughts about the gratitude journal activity. Student sharing is voluntary.",
        "Introduce Gratitude Research: Can anyone guess why we did this experiment? Answer: To learn a skill that can help us have more happiness!",
        "Did you know? Scientists have discovered that people who practice gratitude actually boost their level of happiness! So we have learned another strategy that can help us feel happier.",
        "So far in our MindUP lessons, we have learned how to be mindful of our senses and our thoughts and feelings. Now we are starting to think about other people and things in our lives.",
        "Practicing gratitude helps us think about all of the good or positive things that happen in our lives. This is another strategy to help us be happier and more joyful!",
        "Invite students to consider how practicing gratitude can help our learning community be safe, kind, and focused."
      ],
      followingUp: {
        mindupForHomeAndLife: [
          "As a class, brainstorm ways in which gratitude can apply to students' daily lives.",
          "Find a pebble or stone that feels good to you, and keep it in a safe spot at home or carry it with you in your pocket or backpack. Try holding it in your hand and thinking of three things you are grateful for each day.",
          "Keep a gratitude journal at home where you can draw or write about what you are grateful for.",
          "As you go through your day, pay attention to your surroundings, and notice someone or something you feel lucky you have around you.",
          "Try telling someone how grateful you are to have them in your life!"
        ],
        mindfulMePortfolio: "Invite students to create a portfolio entry about their experience with the MindUP for Home and Life topic, gratitude. This entry could take the form of a picture, poem, song, paragraph, photograph, etc.—the sky's the limit!"
      },
      simplificationIdeas: [
        "Go on a gratitude walk, and chart what students feel they are grateful for.",
        "Invite students to illustrate what they saw on the gratitude walk, and consider making a bulletin board display with the illustrations."
      ],
      challengerIdeas: [
        "Start a Gratitude Journal where students record three things they are grateful for each day, instead of only one.",
        "Create gratitude bracelets or necklaces by placing three beads on a string and tying a knot. Encourage students to touch each of the three beads each day and think about or express three things they are grateful for."
      ],
      literatureList: [
        "The Thankful Book – Todd Parr",
        "The Thank You Letter – Jane Cabrera",
        "Apple Cake – Dawn Casey",
        "Splat the Cat Says Thank You – Rob Scotton",
        "Awesome Is Everywhere – Neil Pasricha",
        "An Awesome Book of Thanks – Dallas Clayton",
        "If I Had a Little Dream – Nina Laden and Melissa Castrillon",
        "We're All Wonders – R. J. Palacio",
        "Look and Be Grateful – Tomie dePaola"
      ]
    }
  },
  // Lesson 16: Acts of Kindness
  {
    id: "k2-u4-l16",
    unit: 4,
    unitTitle: "Mindful of Ourselves in the World",
    lessonNumber: 16,
    title: "Acts of Kindness",
    purpose: "Students learn about how their actions affect others and perform acts of kindness to put their mindful thoughts into action.",
    concepts: [
      "My actions affect others.",
      "Acts of kindness can boost my own happiness.",
      "Being kind to myself is also important."
    ],
    objectives: [
      "Identify ways to perform acts of kindness.",
      "Perform three acts of kindness during the day.",
      "Reflect on how performing acts of kindness makes them feel."
    ],
    socialEmotionalLearning: [
      "Self-awareness",
      "Self-management",
      "Social awareness",
      "Relationship skills",
      "Responsible decision-making"
    ],
    materials: [
      "Chart paper",
      "Paper for recording acts of kindness",
      "Mindful Me Portfolio"
    ],
    estimatedTime: "30 minutes (over 2 periods: Time 1 in morning, Time 2 at end of day)",
    content: {
      brainLink: "In this lesson, students learn about how their actions affect others. This lesson promotes students' connections with others, fostering a harmonious social network. Students perform acts of kindness and reflect on the experience. Mindfully practicing acts of kindness develops students' capacity for empathy and compassion. In turn, experiencing empathy and showing compassion increases children's awareness that they can influence their emotions and feelings and use them in service of others. Acts of kindness are dopamine boosters, and they help develop the neural networks for sensitivity to others' needs. Practicing kindness strengthens our brain—and can make us feel really good!",
      gettingStarted: [
        "Please remember our strategies to be safe, kind, and focused.",
        "Briefly review the previous lesson about gratitude: Practicing gratitude helps us connect with our world in a positive way. Gratitude also helps us keep our focus (thoughts and feelings) on the positive. This is a strategy we can use to boost our happiness.",
        "Ask if anyone would like to share their experiences with their gratitude journal: How do you feel after you've written or drawn about something you're thankful for? What thoughts and feelings did you have while practicing gratitude? What about afterward?"
      ],
      activities: [
        {
          step: 1,
          title: "Mindful Actions: Kindness",
          instructions: [
            "Introduce the lesson by engaging the class in a discussion about mindful choices and actions: Today, we're going to spend some time thinking about how our choices and actions affect us and the people in our community.",
            "Before we begin, think about how it would affect our community and the people in it if you made mindful choices and actions every day.",
            "Invite students to share their ideas.",
            "Now think about how making mindful choices and actions for yourself every day would affect you.",
            "Invite students to share their ideas.",
            "How would our lives be better if the people around us made mindful choices and actions?",
            "Help students connect their ideas with how we're all interconnected.",
            "Have you noticed each other making more mindful choices and actions in the classroom since we have started learning about mindfulness?",
            "Invite students to share examples. Connect their ideas to your MindUP Learning Community Agreement."
          ]
        },
        {
          step: 2,
          title: "Acts of Kindness Activity",
          instructions: [
            "Read the script: Now that we've spent some time thinking about how our actions affect others, let's go out and act mindfully!",
            "For this activity, we are all going to do three acts of kindness today.",
            "First, let's brainstorm what kind acts we can do for others at school today.",
            "Examples: Opening a door for somebody; smiling and saying hello to someone you don't know very well; helping a younger student; cheering someone up if they are having a bad day; acknowledging someone's hard work. What other examples can you think of?",
            "After we're finished doing our kind acts, we'll talk about how it felt to do kind things for others.",
            "Record students' ideas on chart paper.",
            "Have students choose three acts of kindness and record them on a piece of paper. Kindergarten students can record or draw what they want to do.",
            "As students perform the acts throughout the day, they can check them off of their list when they return to their table or desk.",
            "Encourage students to notice how it feels to do kind acts for others."
          ]
        }
      ],
      makingConnections: [
        "Sharing Experiences: At the end of the day, invite students to share their experiences and thoughts about the acts of kindness activity. Student sharing is voluntary.",
        "What did you think about the acts of kindness activity? How did you feel when you did or said something kind?",
        "Introduce Kindness Research: Why do you think we did this activity? Answer: To learn a skill that can help us have more happiness!",
        "Did you know? Scientists discovered that people who perform acts of kindness end up feeling happy, too!",
        "Much like practicing gratitude, making a mindful choice to do kind things for others can help us have more joy and happiness in our lives.",
        "Another thing to consider is the importance of being kind to yourself! Just as you would care for a good friend, it is important to treat yourself the same way.",
        "When we are mindful of ourselves and are gentle and kind to ourselves, we are happier. And when we are happier, we are more likely to take better care of others, too!",
        "So, we have learned another strategy that will help us feel happier and connect us with others in a positive way!",
        "This week, we focused on how our choices and actions affect others.",
        "Our experience performing acts of kindness put our mindful thoughts into action!",
        "While we were helping and connecting with our world in a positive way, we also boosted our own joy and happiness.",
        "Thinking and acting mindfully helps us keep our focus (thoughts and feelings) on the positive. It's another strategy to help us be more joyful and happy.",
        "Invite students to consider how acts of kindness can help our learning community be safe, kind, and focused."
      ],
      followingUp: {
        mindupForHomeAndLife: [
          "As a class, brainstorm ways in which students could continue to perform simple acts of kindness in their daily lives.",
          "Possible acts of kindness: Smile at everyone you meet. Offer to help a neighbor. Help with chores around the house. Read to your siblings. Draw a picture for your caregivers. Tell someone how much you love them. Hold the door for someone. Actively listen to someone who is speaking to you. Invite someone new to play with you. Plant something. Feed your pet. Compliment a friend. Share your toys. Clean up your room.",
          "The possibilities are endless!"
        ],
        mindfulMePortfolio: "Invite students to create a portfolio entry about their experience with the MindUP for Home and Life topic, kindness. How does kindness affect them each day? This entry could take the form of a picture, poem, song, paragraph, photograph, etc.—the sky's the limit!"
      },
      simplificationIdeas: [
        "Read 'Have You Filled a Bucket Today?' from the Literature List, and chart simple ways students can fill each other's buckets.",
        "Invite students to illustrate how others reacted to their acts of kindness."
      ],
      challengerIdeas: [
        "Keep a collection of 'kindness notes' in a special container in the class where students can randomly write down and add kind thoughts they have for others, such as things they notice that they appreciate. Then, at any time during the day, pull out a kindness note and read it to the class.",
        "Encourage students to write cards/notes to various staff at the school thanking them for what they do.",
        "Create a bulletin board where students can post acts of kindness they see others perform.",
        "Create signage to put up around the school with ideas for acts of kindness that others can read and act on."
      ],
      literatureList: [
        "Have You Filled a Bucket Today? – Carol McCloud",
        "The Kindness Book – Todd Parr",
        "Kindness Makes Us Strong – Sophie Beer",
        "Be Kind – Pat Zietlow Miller",
        "Each Kindness – Jacqueline Woodson",
        "The Invisible Boy – Trudy Ludwig",
        "Ordinary Mary's Extraordinary Deed – Emily Pearson",
        "The Jelly Donut Difference – Maria Dismondy"
      ]
    }
  },
  // Lesson 17: Mindful Actions in Our Community
  {
    id: "k2-u4-l17",
    unit: 4,
    unitTitle: "Mindful of Ourselves in the World",
    lessonNumber: 17,
    title: "Mindful Actions in Our Community",
    purpose: "Students expand their understanding of their interconnectedness with the world and how their actions affect others. The class works as a team to perform an act of kindness for their friends or community.",
    concepts: [
      "We are connected to our community.",
      "Working together as a team can make a positive difference.",
      "Mindful actions help both others and ourselves."
    ],
    objectives: [
      "Identify a group act of kindness they can perform as a class.",
      "Plan and execute a group act of kindness.",
      "Reflect on how working together and helping others makes them feel."
    ],
    socialEmotionalLearning: [
      "Self-awareness",
      "Self-management",
      "Social awareness",
      "Relationship skills",
      "Responsible decision-making"
    ],
    materials: [
      "Chart paper",
      "Materials needed for chosen group act of kindness",
      "Optional: Camera to document the activity",
      "Mindful Me Portfolio"
    ],
    estimatedTime: "Varies (completed over 3 separate periods - Day 1: Parts 1 and 2, Day 2: Group Kindness Activity, Day 3: Part 3)",
    content: {
      brainLink: "This lesson builds on the experiential learning activities in the previous lesson, which focused on kindness. Students expand their understanding of their interconnectedness with the world and how their actions affect others. The class works as a team to perform an act of kindness for their friends or community. This is designed to foster autonomy, belonging, and competence—three fundamental human needs! Shared experiences can help groups come together in a positive way. Strategies and activities that build class communities, such as acts of kindness, are especially important when there is more diversity among students. A supportive learning community fosters students' resilience.",
      gettingStarted: [
        "Please remember our strategies to be safe, kind, and focused.",
        "Briefly review Lesson 16: Acts of Kindness: Last week, we focused on how our choices and actions affect others, and we performed acts of kindness to put our mindful thoughts into action!",
        "Connecting with others in this positive way also helps us feel more joy and happiness.",
        "Ask if anyone has performed acts of kindness since last time and, if so, what they noticed (thoughts and feelings)."
      ],
      activities: [
        {
          step: 1,
          title: "Introduce the Group Act of Kindness Activity",
          instructions: [
            "Explain to students that the class will be using mindful choices and actions as a group to make a positive difference in their world.",
            "Read the script: Last time, we learned about how our choices and actions affect not just us but the people around us, like the people in our communities.",
            "Now that we've practiced making mindful choices and actions on our own, we are going to work together to perform a kind act for our school or wider community."
          ]
        },
        {
          step: 2,
          title: "Group Act of Kindness Activity",
          instructions: [
            "Read the script: As a class, we are going to brainstorm what kinds of meaningful actions we could perform for our school or community.",
            "Examples: Cleaning up litter; planting a bee friendly garden; weeding the school garden; making and delivering cards/letters to neighbors or people in small businesses who live/work in the community; etc.",
            "What other examples can you think of?",
            "When we have decided on our idea, we will then spend some time planning.",
            "Encourage students to choose their act of kindness, guiding them toward one that is feasible in your context.",
            "When the class has chosen their act, allow time for plans and preparations."
          ]
        },
        {
          step: 3,
          title: "Carry out the Group Kindness Activity",
          instructions: [
            "Carry out the group kindness activity! You may choose to take photographs or video to document the day for students to reflect on later.",
            "Note: As this lesson can vary in time commitment, teachers can opt to carry this lesson over several days or a couple of weeks."
          ]
        }
      ],
      makingConnections: [
        "Sharing Experiences: Display any photographs from the group act of kindness activity. Invite students to share their thoughts about their experience. Student sharing is voluntary.",
        "What did you think about working together on our act of kindness activity?",
        "How did you feel when you actually did the act of kindness?",
        "How do you think the people we were kind to may have felt about the kind act?",
        "For this lesson, we worked together as a team and focused on how our choices and actions affect others.",
        "We know that helping others is one way to boost our own joy and happiness. So, while we were helping others, we were also helping ourselves be more positive.",
        "We also did something to become stronger as a class. We shared an experience together. Shared experiences help groups come together in a positive way.",
        "Being positively connected to our world through mindful choices and actions is important. It helps our world and it helps us!",
        "Invite students to consider how mindful actions in our community can help our learning community be safe, kind, and focused."
      ],
      followingUp: {
        mindupForHomeAndLife: [
          "As a class, brainstorm ways students could perform mindful actions in their Community.",
          "Community action ideas: Ask family members to collect items for donation, and donate them to a local thrift store. Engage the family in a community clean-up project. Arrange a visit at the neighborhood senior center to spend time with older people. Donate to the local food bank. Volunteer time at a school or community center.",
          "Support students in developing a community service project that is achievable.",
          "Some examples: They could engage in simple acts of kindness with their friends, such as painting stones with encouraging words (hope, love, be brave, etc.) and leaving them in the grass on the way home from school. They could make cards or drawings for local seniors. They could help make a bird feeder or simply sprinkle birdseed for local birds.",
          "Encourage students to develop planning strategies for their group act of kindness by asking them, 'What do you need to do first/next/last?'"
        ],
        mindfulMePortfolio: "Invite students to review their portfolio entries. What have they learned about themselves? About others? Invite students to create a portfolio entry about their experience with the MindUP lessons. How do students view themselves in the world? This entry could take the form of a picture, poem, song, paragraph, photograph, etc.—the sky's the limit!"
      },
      simplificationIdeas: [
        "Support students in developing a community service project that is achievable.",
        "Some examples: They could engage in simple acts of kindness with their friends, such as painting stones with encouraging words (hope, love, be brave, etc.) and leaving them in the grass on the way home from school. They could make cards or drawings for local seniors. They could help make a bird feeder or simply sprinkle birdseed for local birds.",
        "Encourage students to develop planning strategies for their group act of kindness by asking them, 'What do you need to do first/next/last?'"
      ],
      challengerIdeas: [
        "Support students in developing a community service project that is achievable.",
        "Some examples: They could engage in simple acts of kindness with their friends, such as painting stones with encouraging words (hope, love, be brave, etc.) and leaving them in the grass on the way home from school. They could make cards or drawings for local seniors. They could help make a bird feeder or simply sprinkle birdseed for local birds.",
        "Encourage students to develop planning strategies for their group act of kindness by asking them, 'What do you need to do first/next/last?'"
      ],
      literatureList: [
        "The Peace Book – Todd Parr",
        "What Does Peace Feel Like? – Vladimir Radunsky",
        "Can You Say Peace? – Karen Katz",
        "Talk Peace – Sam Williams and Mique Moriuchi",
        "Some Helpful Tips for a Better World – Rebecca Doughty",
        "Somewhere Today: A Book of Peace – Shelley Moore Thomas (grades 3–5)"
      ]
    }
  }
];

/**
 * Get lesson by ID
 */
export function getLessonById(id: string): MindUPLesson | undefined {
  return K2_CURRICULUM.find(lesson => lesson.id === id);
}

/**
 * Get lessons by unit
 */
export function getLessonsByUnit(unit: number): MindUPLesson[] {
  return K2_CURRICULUM.filter(lesson => lesson.unit === unit);
}

/**
 * Get all lessons
 */
export function getAllLessons(): MindUPLesson[] {
  return K2_CURRICULUM;
}

/**
 * Search lessons by keyword
 */
export function searchLessons(keyword: string): MindUPLesson[] {
  const lowerKeyword = keyword.toLowerCase();
  return K2_CURRICULUM.filter(lesson => 
    lesson.title.toLowerCase().includes(lowerKeyword) ||
    lesson.purpose.toLowerCase().includes(lowerKeyword) ||
    lesson.concepts.some(c => c.toLowerCase().includes(lowerKeyword))
  );
}

